import { childElements, forEachElement, moveAllChildren } from "../util/Utils";
import {
  filter,
  filterSheetButton,
  filterSheetFooterTemplate,
  filterSheetTemplate,
  filterTitles,
  localNavigation,
  menuBarButtonForm,
  selectedNavigationTitle,
  sheetContent,
  sheetMenuBar,
} from "./Selectors";
import { OverflowBox, OverflowBoxContainer } from "../overflowBox/OverflowBox";
import { Expander, initializeExpandersIn } from "../expander/Expander";
import { hasChanges } from "../filter/FilterFormActions";
import PartialLock from "../util/PartialLock";
import { FilterAccordion } from "../filter/FilterAccordion";
import { readSanPopularFacetValues, updateSanPopularFacetValues } from "../filter/SveltePopularFacetValues";
import { getActiveSheet } from "../util/NonModuleFunctions";
import { FilterTitle } from "../filter/FilterTitle";
import type { SheetAPI } from "@otto-ec/global-pattern/types/700_Layer_und_Popup/400_pl_sheet/sheetTypes";
import type { Sheet } from "@otto-ec/global-pattern";
import { createSheetTemplate } from "./PaliSheet";
import { FilterSection } from "../filter/FilterSection";
import { FilterTitles } from "../filter/FilterTitles";
import { Filter } from "../filter/Filter";
import type { TrackingLabels } from "../tracking/TrackingLabels";
import { setActiveConfiguration } from "@otto-ec/heureka-components/filter";
import { FacetValueTag } from "../filter/FacetValueTag";
import { isSelected, SvelteFacetValue } from "../filter/SvelteFacetValue";
import { RefinementBar } from "../refinementBar/RefinementBar";

const FACET_CONTENT_SELECTOR = "ul.find_facet__content";
export const FIRST_LEVEL_CHANGE_TRACKING_LABELS: Partial<TrackingLabels> = {
  san_FacetLocation: "first_level",
};
export const SECOND_LEVEL_CHANGE_TRACKING_LABELS: Partial<TrackingLabels> = {
  san_FacetLocation: "second_level",
};
export const SIDEBAR_CHANGE_TRACKING_LABELS: Partial<TrackingLabels> = {
  san_FacetLocation: "sidebar",
};

export default class FilterSheet {
  private static recentDetailedFilterId: string | undefined;

  private constructor(private readonly sheet?: Sheet | SheetAPI) {}

  public static create() {
    return new FilterSheet(FilterSheet.newSheet());
  }

  public static declare() {
    return new FilterSheet(getActiveSheet());
  }

  private static newSheet() {
    return createSheetTemplate(filterTitles(), filterSheetFooterTemplate());
  }

  setSheet() {
    const content = filterTitles();

    if (content && this.sheet) {
      const footer = filterSheetFooterTemplate();
      this.sheet.removeBackButton();
      this.sheet.setContent(content);
      this.sheet.setMenuBar(childElements(footer) || []);
    }
    return this;
  }

  initializeSheet(updateChangeTracking = true) {
    const elem = filterSheetButton();
    if (elem && this.sheet) {
      setActiveConfiguration("sheetFirstLevel");

      const { scrollOffset: scrollOffset } = elem.dataset;
      const content = this.sheet.getContent();

      content.scrollTop = scrollOffset ? Number(scrollOffset) : 0;
      updateSanPopularFacetValues(content, readSanPopularFacetValues());

      FilterSheet.moveDesktopInlinedFiltersToSheet(content, updateChangeTracking);
      OverflowBox.initAll(undefined, content);
      initializeExpandersIn(content);

      /*                                                                 */
      menuBarButtonForm()?.addEventListener("click", () => {
        FacetValueTag.factory.forEach((tag) => {
          tag.updateTrackingLabelsOnReferenced("deleted", "x_all_local");
        });
        SvelteFacetValue.factory
          .all(RefinementBar.facetValueTagsElement())
          .filter(isSelected)
          .forEach((tag) => {
            tag.updateTrackingLabelsOnReferenced("deleted", "x_all_local");
          });
      });
    }
    return this;
  }

  public static scrollToRecentFilter(recentFilterId?: string) {
    FilterSheet.recentDetailedFilterId = recentFilterId || FilterSheet.recentDetailedFilterId;
    if (FilterSheet.recentDetailedFilterId) {
      const filterTitle = Filter.filterId(FilterSheet.recentDetailedFilterId)?.filterTitle?.elem;
      const content = FilterSheet.declare().sheet?.getContent();
      if (filterTitle && content) {
        const footerHeight = FilterTitle.footer(content)?.elem.clientHeight || 0;
        const footerTop = content.scrollTop + content.clientHeight + content.clientTop - footerHeight;

        if (filterTitle.offsetTop + filterTitle.clientHeight >= footerTop && filterTitle.scrollIntoView) {
          filterTitle.scrollIntoView(true);
        }
      }
    }
  }

  public lockContent() {
    if (this.sheet) {
      this.sheet.getContent().classList.add("pl_sheet__content--loading");
    }
    return this;
  }

  public unlockContent() {
    if (this.sheet) {
      this.sheet.getContent().classList.remove("pl_sheet__content--loading");
    }
    return this;
  }

  public open() {
    if (this.sheet) {
      FilterSheet.recentDetailedFilterId = undefined;
      this.sheet.open();
    }
    return this;
  }

  public close() {
    if (this.sheet) {
      this.sheet.close();
    }
  }

  public ifOpen(callback: (sheet: Sheet | SheetAPI) => void) {
    if (this.sheet) {
      callback(this.sheet);
    }
  }

  isOpen() {
    return !!this.sheet;
  }

  deactivate(trackingLabel: Partial<TrackingLabels>) {
    const source = sheetContent();
    if (source) {
      delete source.dataset.filterId;
      delete source.dataset.tsLabels;
      FilterSheet.closeAllTooltips(source);
      FilterSheet.fireDelayedActionsNowOrNever();
      FilterSheet.moveInlinedFiltersFrom(source, true, trackingLabel);
    }

    moveAllChildren(source, filterSheetTemplate(), true);
    moveAllChildren(sheetMenuBar(), filterSheetFooterTemplate(), true);
  }

  public static addLocalNavigationTitleValue() {
    const title = selectedNavigationTitle(localNavigation());
    const titles = filterTitles();
    if (titles && title) {
      const { overlayEntryTitle = "" } = title.dataset;
      forEachElement(".js_selectedNavigationFilterSheet", (elem) => (elem.innerText = overlayEntryTitle), titles);
    }
  }

  public static addInitialLoadError() {
    const errorMessage = FilterSection.template()?.errorMessage;
    if (errorMessage) {
      FilterTitles.template()?.elem.append(errorMessage.cloneNode(true));
    }
  }

  public static addErrorsOnFilterTitleValues(filterIds?: string[]) {
    const titles = filterTitles();
    if (titles) {
      const invalidIds = filterIds || FilterAccordion.invalidFilterIds();

      invalidIds.forEach((filterId) =>
        forEachElement(
          `.find_filterTitles__item[data-filter-id="${filterId}"]`,
          (item) => item.classList.add("find_filterTitles__item--invalid"),
          titles,
        ),
      );
    }
  }

  public static initPartialLocking() {
    const titles = filterTitles();
    const lockSelector = ".find_filterTitles__item, .heureka_filterTitle";
    /*                               */
    if (titles) {
      PartialLock.create(
        titles,
        lockSelector,
        (lockOwner) => {
          const filterId = lockOwner.dataset.filterId;
          const filterForm = filterId && filter(filterId);
          if (filterForm) {
            filterForm.delayedAction?.nowOrNever();
            return hasChanges(filterForm);
          }
          return false;
        },
        {
          unlockTopics: [
            "heureka.filterSection.loadAborted",
            "ftfind.dresonRule.resolveAborted",
            "ftfind.dresonRule.resolved",
          ],
        },
      );
    }
  }

  public static fireDelayedActionsNowOrNever() {
    const titles = filterTitles();
    if (titles) {
      FilterTitle.factory.forEach((filterTitle) => filterTitle.filter?.form?.delayedAction?.nowOrNever());
    }
  }

  public setScrollOffset(offset?: number) {
    if (typeof offset === "number") {
      const content = this.sheet?.getContent();
      if (content) {
        content.scrollTop = offset;
      }
    }
    return this;
  }

  static moveDesktopInlinedFiltersToSheet(content: HTMLElement, updateChangeTracking: boolean) {
    FilterTitle.factory.forEach((filterTitle) => {
      if (updateChangeTracking) {
        Filter.updateChangeTrackingLabels(filterTitle.filterId, FIRST_LEVEL_CHANGE_TRACKING_LABELS);
      }
      filterTitle.attachFilter();
    });

    OverflowBoxContainer.forEachOverflowBoxContainer(
      (overflowBoxContainer) => overflowBoxContainer.selectAndSetContent(FACET_CONTENT_SELECTOR),
      content,
    );

    Expander.forEachExpander((expander) => expander.selectAndSetContent(FACET_CONTENT_SELECTOR), content);
  }

  static moveInlinedFiltersFrom(
    content: HTMLElement,
    updateChangeTracking: boolean,
    trackingLabel: Partial<TrackingLabels>,
  ) {
    OverflowBoxContainer.forEachOverflowBoxContainer(
      (overflowBoxContainer) => overflowBoxContainer.removeContent(),
      content,
    );
    Expander.forEachExpander((expander) => expander.removeContent(), content);

    FilterTitle.factory.forEach((filterTitle) => {
      if (updateChangeTracking) {
        Filter.updateChangeTrackingLabels(filterTitle.filterId, trackingLabel);
      }
      filterTitle.detachFilter();
    });
  }

  private static closeAllTooltips(sheetContent: HTMLElement) {
    /*                                                                                          */
    forEachElement(
      ".pl_tooltip--bottom .pl_icon",
      (tooltip) => tooltip.dispatchEvent(new Event("click", { bubbles: true })),
      sheetContent,
    );
  }
}
